<script setup lang="ts">
  import { VPlusOutlined } from "@krainovsd/vue-icons";
  import { VButton, VText } from "@krainovsd/vue-ui";
  import { computed, onMounted } from "vue";
  import { useI18n } from "vue-i18n";
  import { useRouter } from "vue-router";
  import {
    COMPARISON_METHOD_LABELS,
    formatComparisonPeriod,
    useComparisonStore,
  } from "@/entities/comparison";
  import { PAGES } from "@/entities/tech";
  import Card from "@/components/atoms/Card.vue";

  const { t } = useI18n();
  const router = useRouter();
  const comparisonStore = useComparisonStore();

  onMounted(() => void comparisonStore.load());

  const previousPeriodLabel = computed(() =>
    t("pages.comparison.previousPeriod", {}, { default: "Прошлый период" }),
  );

  // List rows derived from the on-device comparisons:
  //   name     — `${targetValueOne} vs ${targetValueTwo || "Прошлый период"} • ${method}`
  //   subtitle — `${method} • ${periodDates} • ${createdDate}`
  const items = computed(() =>
    comparisonStore.items.map((record) => {
      const versus = record.targetValueTwo || previousPeriodLabel.value;
      const method = COMPARISON_METHOD_LABELS[record.method];
      // Same routing rule as CreateComparisonPage's submit: a Bayesian
      // "anotherTarget" comparison opens the Bayesian page, everything else the
      // factor analysis.
      const isBayesian = record.mode === "anotherTarget" && record.method === "bayesian";
      return {
        id: record.id,
        name: `${record.targetValueOne} vs ${versus} • ${method}`,
        subtitle: `${method} • ${formatComparisonPeriod(record.period)} • ${record.createdDate}`,
        route: isBayesian ? PAGES.BayesianCompare : PAGES.FactorComparison,
      };
    }),
  );

  function handleCreate() {
    void router.push({ name: PAGES.CreateComparison });
  }

  function openComparison(item: { id: number; route: string }) {
    void router.push({ name: item.route, params: { id: String(item.id) } });
  }
</script>

<template>
  <div :class="$style.base">
    <h1>{{ $t("pages.comparison.title", {}, { default: "Сравнение" }) }}</h1>
    <VButton size="large" :class="$style.newButton" @click="handleCreate">
      <template #icon>
        <VPlusOutlined />
      </template>
      {{ $t("pages.comparison.newButton", {}, { default: "Новое сравнение" }) }}
    </VButton>

    <section :class="$style.list">
      <span :class="$style.blockTitle">
        {{ $t("pages.comparison.compareHistory", {}, { default: "История сравнений" }) }}
      </span>
      <div v-if="items.length" :class="$style.items">
        <Card
          v-for="item in items"
          :key="item.id"
          :class="$style.item"
          @click="openComparison(item)"
        >
          <span :class="$style.itemTitle">{{ item.name }}</span>
          <span :class="$style.itemSubtitle">{{ item.subtitle }}</span>
        </Card>
      </div>
      <div v-else-if="comparisonStore.loaded" :class="$style.empty">
        <VText :class="$style.emptyText">
          {{
            $t(
              "pages.comparison.empty",
              {},
              { default: "Здесь будут ваши сравнения. Создайте первое через «Новое сравнение»." },
            )
          }}
        </VText>
      </div>
    </section>
  </div>
</template>

<style lang="scss" module>
  .base {
    width: 100%;
    height: 100%;
    max-height: 100%;
    padding: 16px 20px;

    overflow: auto;
    overscroll-behavior: contain;

    display: flex;
    gap: 24px;
    flex-direction: column;

    // WebKit doesn't include the container's padding-bottom in the scrollable
    // area, so a flex-item spacer clears the fixed BottomNavigation on every
    // engine (matches StatisticsPage).
    &::after {
      content: "";
      flex-shrink: 0;
      height: calc(57px + 32px + env(safe-area-inset-bottom, 0px));
    }
  }

  .newButton {
    padding: 0.5rem 1rem;

    display: flex;
    align-items: center;
    gap: 0.5rem;

    font-size: 0.875rem;
    line-height: 1.25rem;
    font-weight: 500;

    background-color: var(--brand);
    color: var(--brand-foreground);
  }

  .list {
    width: 100%;
    flex: 1 0 auto;

    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .blockTitle {
    color: var(--muted-foreground);

    font-weight: 500;
    font-size: 0.75rem;
    line-height: 1rem;

    text-transform: uppercase;

    letter-spacing: 0.14em;
  }

  .items {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .item {
    padding: 12px 8px;

    display: flex;
    flex-direction: column;
    gap: 2px;

    cursor: pointer;
  }

  .itemTitle {
    font-size: 1rem;
    line-height: 1.5rem;
    color: var(--foreground);
  }

  .itemSubtitle {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--muted-foreground);
  }

  .empty {
    padding: 1.5rem;
    border: 1px dashed var(--border);
  }

  .emptyText {
    color: var(--muted-foreground);
    font-size: 0.875rem;
    line-height: 1.25rem;
  }
</style>
